'use client';

import { Fragment } from 'react';
import { PortableText } from 'next-sanity';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { useRef } from 'react';

import { GroqImage } from '@/helpers/Image';
import {
  PageHomeHowItWorksStep,
  PageHomeSchema,
} from '@/sanity/pages/PageHomeTemplate';
import { richTextComponent } from '@/sanity/sanityPortableText';

// ─── Step panels ────────────────────────────────────────────────────────────

function StepVisual({ step }: { step: PageHomeHowItWorksStep }) {
  return (
    <div className="relative h-[49.00rem] lg:w-[54.9rem]  overflow-hidden ">
      {step.image ? (
        <GroqImage
          src={step.image}
          alt={step.stepLabel || ''}
          className="absolute inset-0 !h-full !w-full !object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-[#103037]" />
      )}
    </div>
  );
}

function StepText({
  step,
  index,
}: {
  step: PageHomeHowItWorksStep;
  index: number;
}) {
  return (
    <div className="flex w-full flex-col justify-center gap-[1.75rem] lg:gap-[2.25rem] lg:w-[55rem]">
      <div className="flex flex-col gap-[1.75rem] lg:gap-[2.25rem]">
        <p className="mb-0 font-blacker text-[6rem] italic leading-[1.33] text-[#003828]">
          {String(index + 1).padStart(2, '0')}
        </p>
        {step.stepLabel && (
          <p className="mb-0 font-geist-mono text-[1.75rem] font-semibold uppercase leading-[1.33] text-[#003828]">
            {step.stepLabel}
          </p>
        )}
      </div>
      {step.body && (
        <div className="richtext richtext-start flex flex-col gap-[2.25rem] prose-headings:mb-0 prose-headings:mt-0 prose-headings:font-geist prose-headings:text-[2.25rem] lg:prose-headings:text-[3rem] prose-headings:font-medium prose-headings:leading-[1.3] prose-headings:tracking-[-0.09rem] prose-headings:text-black prose-p:mb-0 prose-p:[text-wrap:wrap] prose-p:font-geist prose-p:text-[1.75rem] lg:prose-p:text-[2rem] prose-p:leading-[1.62] prose-p:tracking-[-0.08rem] prose-p:text-black/70">
          <PortableText value={step.body} components={richTextComponent} />
        </div>
      )}
    </div>
  );
}

// ─── Section ─────────────────────────────────────────────────────────────────

export default function HowItWorksSection({ data }: { data: PageHomeSchema }) {
  const steps = data.howItWorksSteps ?? [];
  const stepsRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: stepsRef,
    offset: ['start center', 'end center'],
  });
  const smooth = useSpring(scrollYProgress, {
    stiffness: 200,
    damping: 40,
    restDelta: 0.001,
  });
  const scaleY = useTransform(smooth, [0, 1], [0, 1]);
  const dotY = useTransform(smooth, [0, 1], ['0%', '100%']);

  if (!data.howItWorksTitle && !steps.length) return null;

  return (
    <section className="relative w-full overflow-hidden bg-white pb-[0rem] pt-[8rem] lg:pb-[15rem] lg:pt-[15rem]">
      <div className="flex flex-col items-center gap-[8rem] lg:gap-[21rem]">
        {/* Header */}
        <div className="mx-auto flex w-[min(180rem,calc(100%-8rem))] flex-col items-center gap-[2rem] text-center">
          {data.howItWorksChip && (
            <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5] text-black">
              {data.howItWorksChip}
            </p>
          )}
          {data.howItWorksTitle && (
            <div className="richtext w-full flex flex-col gap-[2rem] prose-p:max-w-[60rem]">
              <PortableText
                value={data.howItWorksTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {/* Steps */}
        <div ref={stepsRef} className="relative flex flex-col items-center">
          {/* Center vertical connector line — desktop only */}
          <div className="pointer-events-none absolute inset-y-0 left-1/2 hidden -translate-x-1/2 lg:block">
            <div className="h-full w-px bg-[#228e71]/20" />
            <motion.div
              className="absolute inset-x-0 top-0 w-px origin-top bg-[#228e71]"
              style={{ scaleY, height: '100%' }}
            />
            <motion.div
              className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ top: dotY }}
            >
              <svg
                className="h-[2.5rem] w-[2.5rem]"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="none"
              >
                <circle cx="10" cy="10" r="10" fill="#228E71" />
                <circle cx="10" cy="10" r="5" fill="#fff" />
              </svg>
            </motion.div>
          </div>

          <div className="flex flex-col items-center gap-[8rem] lg:gap-[30rem]">
            {steps.map((step, index) => {
              const isImageLeft = step.mediaPosition === 'left';
              return (
                <Fragment key={step._key ?? index}>
                  <div
                    className={`relative flex w-[min(140rem,calc(100%-4rem))] flex-col gap-[4rem] ${isImageLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} lg:gap-[31rem]`}
                  >
                    <StepVisual step={step} />
                    <StepText step={step} index={index} />
                  </div>
                  {index < steps.length - 1 && (
                    <div className="w-[min(140rem,calc(100%-4rem))] h-px bg-black/10 lg:hidden" />
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
